// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { assemblySchema } from '@/assembly/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { dateSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Schema for the create assembly request.
 */
const _createAssemblyRequestSchema = z.object({
  productId: z.string().uuid(),
  serial: z.string().nullable(),
  locationId: z.string().uuid(),
  buildDate: dateSchema,
  parts: z.array(
    z.object({
      partId: z.string().uuid(),
      quantity: z.number(),
      serials: z.array(z.string()),
    })
  ),
  notes: z.string().nullable(),
});

/**
 * Type for the create assembly request.
 */
type CreateAssemblyRequest = z.infer<typeof _createAssemblyRequestSchema>;

/**
 * Schema for the create assembly response.
 */
const createAssemblyResponseSchema = z.object({
  data: assemblySchema,
});

/**
 * Type for the create assembly response.
 */
type CreateAssemblyResponse = z.infer<typeof createAssemblyResponseSchema>;

/**
 * Creates a new assembly.
 * @param request The assembly data
 * @returns Promise resolving to the created assembly
 */
export async function createAssembly(
  request: CreateAssemblyRequest
): Promise<CreateAssemblyResponse> {
  const url = '/v1/assembly';
  const response = await api.post(url, {
    ...request,
    buildDate: serializeDateForAPI(request.buildDate),
  });
  return parseResponse(createAssemblyResponseSchema, response.data, {
    schemaName: 'CreateAssembly',
    endpoint: url,
  });
}
